import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  UpdateProjectByIdParams,
  UpdateProjectByIdBody,
  UpdateProjectByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface UpdateProjectByIdState
  extends MergeRequestState<UpdateProjectByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.updateProjectById,
  isFetching: (state: ReduxState) => state.server.updateProjectById.isFetching,
  data: (state: ReduxState) => state.server.updateProjectById.data,
  error: (state: ReduxState) => state.server.updateProjectById.error,
};

const initialState: UpdateProjectByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/updateProjectById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      body,
      config,
    }: {
      params: UpdateProjectByIdParams;
      body: UpdateProjectByIdBody;
      config?: ServerApiConfig;
    },
    { rejectWithValue },
  ) => serverApi.updateProjectById(params, body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerUpdateProjectById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const updateProjectById: ServerUpdateProjectById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
